import { toast } from "sonner";
import { getPyodide } from "./pyodideLoader";
import { testCases, type TestCase } from "@/data/test-cases";

export interface TestResult {
  testId: string;
  description: string;
  passed: boolean;
  actualOutput?: any;
  expectedOutput?: any;
  errorMessage?: string;
  executionTime?: number;
  isHidden?: boolean;
}

export interface ExecutionResult {
  success: boolean;
  output: string;
  error?: string;
  testResults?: TestResult[];
  passedTests?: number;
  totalTests?: number;
}

export const getCodeTemplate = (challengeId: string): string => {
  const templates: Record<string, string> = {
    "sigmoid-function": `import numpy as np

def sigmoid(x):
    \"\"\"
    Compute the sigmoid of x
    
    Arguments:
    x -- A scalar or numpy array
    
    Return:
    s -- sigmoid(x)
    \"\"\"
    # YOUR CODE HERE
    
    return s`,

    "cross-entropy-loss": `import numpy as np

def cross_entropy(y_pred, y_true):
    \"\"\"
    Compute cross-entropy loss
    
    Arguments:
    y_pred -- numpy array of predicted probabilities (0 to 1)
    y_true -- numpy array of true labels (0 or 1)
    
    Return:
    loss -- cross entropy loss value
    \"\"\"
    # YOUR CODE HERE
    
    return loss`,

    "decision-stump": `import numpy as np

def find_best_split(X, y):
    """
    Find best split for decision stump
    
    Arguments:
    X -- features array of shape (n_samples, n_features)
    y -- labels array of shape (n_samples,)
    
    Return:
    dict with 'threshold' and 'feature' keys
    """
    # YOUR CODE HERE
    
    return {'threshold': threshold, 'feature': feature}`,

    "mean-variance": `import numpy as np

def calculate_mean(data):
    """
    Calculate the mean of a dataset
    
    Arguments:
    data -- numpy array of data points
    
    Return:
    mean -- mean value of the data
    """
    # YOUR CODE HERE
    
    return mean
    
def calculate_variance(data):
    """
    Calculate the variance of a dataset
    
    Arguments:
    data -- numpy array of data points
    
    Return:
    variance -- variance value of the data
    """
    # YOUR CODE HERE
    
    return variance`,

    "knn-classifier": `import numpy as np

class KNNClassifier:
    def __init__(self, k=3):
        self.k = k
        self.X_train = None
        self.y_train = None
        
    def fit(self, X, y):
        """
        Store the training data
        
        Arguments:
        X -- training features of shape (n_samples, n_features)
        y -- training labels of shape (n_samples,)
        """
        # YOUR CODE HERE
        
        pass
        
    def predict(self, X):
        """
        Predict labels for test data
        
        Arguments:
        X -- test features of shape (n_samples, n_features)
        
        Return:
        y_pred -- predicted labels for X
        """
        # YOUR CODE HERE
        
        return y_pred`,

    "gradient-mse": `import numpy as np

def gradient_mse(X, y, theta):
    """
    Compute gradient of Mean Squared Error
    
    Arguments:
    X -- features matrix of shape (n_samples, n_features)
    y -- target values of shape (n_samples,)
    theta -- parameters of shape (n_features,)
    
    Return:
    grad -- gradient of MSE with respect to theta
    """
    # YOUR CODE HERE
    
    return grad`,

    "knn": `import numpy as np

def knn_predict(X_train, y_train, X_test, k):
    \"\"\"
    K-Nearest Neighbors prediction
    
    Arguments:
    X_train -- training features
    y_train -- training labels
    X_test -- test features
    k -- number of neighbors
    
    Return:
    y_pred -- predicted labels
    \"\"\"
    # YOUR CODE HERE
    
    return y_pred`,

    "logistic-regression": `import numpy as np

def logistic_regression(X, y, iterations=100):
    \"\"\"
    Train logistic regression
    
    Arguments:
    X -- training features
    y -- training labels
    iterations -- number of training iterations
    
    Return:
    dict with 'converged' key
    \"\"\"
    # YOUR CODE HERE
    
    return {'converged': True}`,
  };
  
  return templates[challengeId] || `# No template available for this challenge

# Write your solution here`;
};

// Sample test cases for each challenge
export const getTestCases = (challengeId: string) => {
  const testCaseDescriptions: Record<string, string[]> = {
    "sigmoid-function": [
      "sigmoid(0) should be close to 0.5",
      "sigmoid(10) should be close to 1",
      "sigmoid(-10) should be close to 0",
      "sigmoid(np.array([0, 1, 2])) should work with arrays"
    ],
    "cross-entropy-loss": [
      "Basic cross-entropy calculation with [0.9, 0.1, 0.8] and [1, 0, 1]",
      "Perfect uncertainty case with [0.5, 0.5] and [1, 0]"
    ],
    "mean-variance": [
      "Calculate mean and variance for simple dataset [1, 2, 3, 4, 5]",
      "Variance should be 0 for uniform data [10, 10, 10, 10]",
      "Calculate for more complex dataset"
    ],
    "decision-stump": [
      "Find optimal threshold for simple dataset"
    ],
    "knn": [
      "KNN classification with k=2"
    ],
    "logistic-regression": [
      "Logistic regression should converge"
    ],
  };
  
  return testCaseDescriptions[challengeId] || ["No test cases available for this challenge"];
};

// Execute code using Pyodide
export const executeCode = async (
  code: string,
  challengeId: string
): Promise<ExecutionResult> => {
  console.log("Executing code for challenge:", challengeId);

  // Basic syntax check
  if (!code.trim()) {
    return {
      success: false,
      output: "",
      error: "Error: Code cannot be empty",
    };
  }

  // Get test cases for this challenge
  const tests = testCases[challengeId];
  if (!tests || tests.length === 0) {
    return {
      success: false,
      output: "",
      error: "No test cases defined for this challenge",
    };
  }

  try {
    // Load Pyodide
    const pyodide = await getPyodide();
    
    const testResults: TestResult[] = [];
    let passedTests = 0;

    // Run each test case
    for (const test of tests) {
      const startTime = performance.now();
      
      try {
        // Load the user's code
        await pyodide.runPythonAsync(code);

        // Prepare test execution based on challenge type
        let testCode = "";
        let actualOutput: any;

        if (challengeId === "sigmoid-function") {
          testCode = `
import numpy as np
import json
result = sigmoid(${JSON.stringify(test.input)})
json.dumps(result.tolist() if hasattr(result, 'tolist') else result)
          `;
        } else if (challengeId === "cross-entropy-loss") {
          testCode = `
import numpy as np
import json
y_pred = np.array(${JSON.stringify(test.input.y_pred)})
y_true = np.array(${JSON.stringify(test.input.y_true)})
result = cross_entropy(y_pred, y_true)
json.dumps(float(result))
          `;
        } else if (challengeId === "decision-stump") {
          testCode = `
import numpy as np
import json
X = np.array(${JSON.stringify(test.input.X)})
y = np.array(${JSON.stringify(test.input.y)})
result = find_best_split(X, y)
json.dumps(result)
          `;
        } else if (challengeId === "knn") {
          testCode = `
import numpy as np
import json
X_train = np.array(${JSON.stringify(test.input.X_train)})
y_train = np.array(${JSON.stringify(test.input.y_train)})
X_test = np.array(${JSON.stringify(test.input.X_test)})
result = knn_predict(X_train, y_train, X_test, ${test.input.k})
json.dumps(result.tolist() if hasattr(result, 'tolist') else result)
          `;
        } else if (challengeId === "mean-variance") {
          testCode = `
import numpy as np
import json
data = np.array(${JSON.stringify(test.input)})
mean_result = calculate_mean(data)
variance_result = calculate_variance(data)
json.dumps({"mean": float(mean_result), "variance": float(variance_result)})
          `;
        } else {
          // Generic test execution
          testCode = `
import json
result = ${test.input}
json.dumps(result)
          `;
        }

        const output = await pyodide.runPythonAsync(testCode);
        actualOutput = JSON.parse(output);

        const executionTime = Math.round(performance.now() - startTime);

        // Compare output with expected
        const passed = compareOutputs(
          actualOutput,
          test.expectedOutput,
          test.tolerance
        );

        if (passed) passedTests++;

        testResults.push({
          testId: test.id,
          description: test.description,
          passed,
          actualOutput,
          expectedOutput: test.expectedOutput,
          executionTime,
          isHidden: test.isHidden,
        });
      } catch (error) {
        const executionTime = Math.round(performance.now() - startTime);
        testResults.push({
          testId: test.id,
          description: test.description,
          passed: false,
          errorMessage: error instanceof Error ? error.message : String(error),
          expectedOutput: test.expectedOutput,
          executionTime,
          isHidden: test.isHidden,
        });
      }
    }

    const allPassed = passedTests === tests.length;
    
    if (allPassed) {
      markChallengeAsCompleted(challengeId);
    }
    
    return {
      success: allPassed,
      output: allPassed
        ? `✅ All ${tests.length} test cases passed! 🎉`
        : `❌ ${passedTests}/${tests.length} test cases passed`,
      testResults,
      passedTests,
      totalTests: tests.length,
    };
  } catch (error) {
    return {
      success: false,
      output: "",
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

// Helper function to compare outputs with tolerance for floats
function compareOutputs(
  actual: any,
  expected: any,
  tolerance: number = 0.0001
): boolean {
  if (typeof actual === "number" && typeof expected === "number") {
    return Math.abs(actual - expected) < tolerance;
  }

  if (Array.isArray(actual) && Array.isArray(expected)) {
    if (actual.length !== expected.length) return false;
    return actual.every((val, idx) =>
      compareOutputs(val, expected[idx], tolerance)
    );
  }

  if (typeof actual === "object" && typeof expected === "object") {
    const actualKeys = Object.keys(actual);
    const expectedKeys = Object.keys(expected);
    if (actualKeys.length !== expectedKeys.length) return false;
    return actualKeys.every((key) =>
      compareOutputs(actual[key], expected[key], tolerance)
    );
  }

  return actual === expected;
}

// Function to save code to localStorage
export const saveCodeToLocalStorage = (challengeId: string, code: string): void => {
  try {
    localStorage.setItem(`ml_playground_${challengeId}`, code);
  } catch (error) {
    console.error("Error saving code to localStorage:", error);
  }
};

// Function to load code from localStorage
export const loadCodeFromLocalStorage = (challengeId: string, defaultCode: string): string => {
  try {
    const savedCode = localStorage.getItem(`ml_playground_${challengeId}`);
    return savedCode || defaultCode;
  } catch (error) {
    console.error("Error loading code from localStorage:", error);
    return defaultCode;
  }
};

// Function to clear saved code
export const clearSavedCode = (challengeId: string): void => {
  try {
    localStorage.removeItem(`ml_playground_${challengeId}`);
  } catch (error) {
    console.error("Error clearing saved code:", error);
  }
};

// New functions to track challenge completion status
export const markChallengeAsCompleted = (challengeId: string): void => {
  try {
    const completedChallenges = getCompletedChallenges();
    if (!completedChallenges.includes(challengeId)) {
      completedChallenges.push(challengeId);
      localStorage.setItem('ml_playground_completed_challenges', JSON.stringify(completedChallenges));
    }
  } catch (error) {
    console.error("Error marking challenge as completed:", error);
  }
};

export const getCompletedChallenges = (): string[] => {
  try {
    const completedChallenges = localStorage.getItem('ml_playground_completed_challenges');
    return completedChallenges ? JSON.parse(completedChallenges) : [];
  } catch (error) {
    console.error("Error getting completed challenges:", error);
    return [];
  }
};

export const isChallengeCompleted = (challengeId: string): boolean => {
  return getCompletedChallenges().includes(challengeId);
};
